"use client";

import { createContext, useContext, useState, useRef, useCallback, useMemo } from "react";
import type { ColumnFilter, NumFilter } from "@/lib/filtering";
import type { ViewConfig } from "@/lib/views";

export type { NumFilter };

type HeaderContextType = {
  search: string;
  setSearch: (v: string) => void;
  attrFilters: Record<string, string[]>;
  setAttrFilter: (key: string, values: string[]) => void;
  setAllAttrFilters: (filters: Record<string, string[]>) => void;
  numFilters: NumFilter[];
  setNumFilter: (key: string, min: number | null, max: number | null) => void;
  clearNumFilter: (key: string) => void;
  columnFilters: ColumnFilter[];
  setColumnFilter: (key: string, patch: Partial<ColumnFilter>) => void;
  setColumnValueSelection: (key: string, patch: Pick<ColumnFilter, "selectedValues" | "excludedValues">) => void;
  clearColumnFilter: (key: string) => void;
  clearColumnFilters: () => void;
  clearAllFilters: () => void;
  sortKey: string | null;
  sortDir: "asc" | "desc";
  setSort: (key: string | null, dir?: "asc" | "desc") => void;
  hasNoteFilter: boolean;
  setHasNoteFilter: (v: boolean) => void;
  noteOfferIds: Set<string>;
  setNoteOfferIds: (s: Set<string>) => void;
  hasFavFilter: boolean;
  setHasFavFilter: (v: boolean) => void;
  onFavFilterToggle: React.MutableRefObject<((val: boolean) => void) | null>;
  promoFilter: string[];
  setPromoFilter: (v: string[]) => void;
  promoFilterVisible: boolean;
  setPromoFilterVisible: (visible: boolean) => void;
  filteredCount: number | null;
  totalCount: number | null;
  setProductCounts: (filtered: number, total: number) => void;
  storeScopeControl: HeaderStoreScopeControl | null;
  setStoreScopeControl: (control: HeaderStoreScopeControl | null) => void;
};

const HeaderContext = createContext<HeaderContextType | null>(null);

export type HeaderStoreScopeControl = {
  storeScope: NonNullable<ViewConfig["storeScope"]>;
  selectedStoreIds: string[];
  onChange: (storeScope: NonNullable<ViewConfig["storeScope"]>, selectedStoreIds: string[]) => void;
};

export function HeaderProvider({ children }: { children: React.ReactNode }) {
  const [search, setSearch] = useState("");
  const [attrFilters, setAttrFilters] = useState<Record<string, string[]>>({});
  const [numFilters, setNumFilters] = useState<NumFilter[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [hasNoteFilter, setHasNoteFilter] = useState(false);
  const [noteOfferIds, setNoteOfferIds] = useState<Set<string>>(new Set());
  const [hasFavFilter, setHasFavFilter] = useState(false);
  const onFavFilterToggle = useRef<((val: boolean) => void) | null>(null);
  const [promoFilter, setPromoFilter] = useState<string[]>([]);
  const [promoFilterVisible, setPromoFilterVisibleState] = useState(false);
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [storeScopeControl, setStoreScopeControl] = useState<HeaderStoreScopeControl | null>(null);

  // Все коллбэки обёрнуты в useCallback с пустым deps — они используют только
  // setState-сеттеры (которые стабильны) или функциональную форму setX(prev => ...).
  // Без этого каждый рендер провайдера создавал бы новые ссылки на функции,
  // и потребители useHeader() перерисовывались бы лишний раз, плюс useEffect-ы
  // в дочерних компонентах не могли бы корректно полагаться на эти функции в deps.

  const setProductCounts = useCallback((filtered: number, total: number) => {
    setFilteredCount(filtered);
    setTotalCount(total);
  }, []);

  const setAttrFilter = useCallback((key: string, values: string[]) => {
    setAttrFilters(prev => {
      if (values.length === 0) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: values };
    });
  }, []);

  const setAllAttrFilters = useCallback((filters: Record<string, string[]>) => {
    setAttrFilters(filters);
  }, []);

  const setNumFilter = useCallback((key: string, min: number | null, max: number | null) => {
    setNumFilters(prev => {
      const existing = prev.findIndex(f => f.key === key);
      const entry = { key, min, max };
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = entry;
        return next;
      }
      return [...prev, entry];
    });
  }, []);

  const clearNumFilter = useCallback((key: string) => {
    setNumFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const setColumnFilter = useCallback((key: string, patch: Partial<ColumnFilter>) => {
    setColumnFilters(prev => {
      const current = prev.find(filter => filter.key === key);
      const nextFilter: ColumnFilter = {
        key,
        mode: patch.mode ?? current?.mode ?? "all",
        value: patch.value ?? current?.value ?? "",
        min: patch.min ?? current?.min ?? "",
        max: patch.max ?? current?.max ?? "",
      };
      const selectedValues = patch.selectedValues ?? current?.selectedValues;
      const excludedValues = patch.excludedValues ?? current?.excludedValues;
      if (Array.isArray(selectedValues)) nextFilter.selectedValues = selectedValues;
      else if (Array.isArray(excludedValues)) nextFilter.excludedValues = excludedValues;
      return [...prev.filter(filter => filter.key !== key), nextFilter];
    });
  }, []);

  const setColumnValueSelection = useCallback((key: string, patch: Pick<ColumnFilter, "selectedValues" | "excludedValues">) => {
    setColumnFilters(prev => {
      const current = prev.find(filter => filter.key === key);
      const nextFilter: ColumnFilter = {
        key,
        mode: current?.mode ?? "all",
        value: current?.value ?? "",
        min: current?.min ?? "",
        max: current?.max ?? "",
      };
      if (Array.isArray(patch.selectedValues)) nextFilter.selectedValues = patch.selectedValues;
      else if (Array.isArray(patch.excludedValues)) nextFilter.excludedValues = patch.excludedValues;
      return [...prev.filter(filter => filter.key !== key), nextFilter];
    });
  }, []);

  const clearColumnFilter = useCallback((key: string) => {
    setColumnFilters(prev => prev.filter(filter => filter.key !== key));
  }, []);

  const clearColumnFilters = useCallback(() => {
    setColumnFilters([]);
  }, []);

  const setSort = useCallback((key: string | null, dir: "asc" | "desc" = "desc") => {
    setSortKey(key);
    setSortDir(dir);
  }, []);

  const clearAllFilters = useCallback(() => {
    setAttrFilters({});
    setNumFilters([]);
    setColumnFilters([]);
    setSortKey(null);
    setSortDir("desc");
    setHasNoteFilter(false);
    setHasFavFilter(false);
    setPromoFilter([]);
  }, []);

  const setPromoFilterVisible = useCallback((visible: boolean) => {
    setPromoFilterVisibleState(visible);
    if (!visible) setPromoFilter([]);
  }, []);

  // Value тоже мемоизируется — иначе литерал-объект пересоздавался бы каждый
  // рендер и обнулял бы все оптимизации выше.
  const value = useMemo<HeaderContextType>(() => ({
    search, setSearch,
    attrFilters, setAttrFilter, setAllAttrFilters,
    numFilters, setNumFilter, clearNumFilter,
    columnFilters, setColumnFilter, setColumnValueSelection, clearColumnFilter, clearColumnFilters,
    clearAllFilters,
    sortKey, sortDir, setSort,
    hasNoteFilter, setHasNoteFilter,
    noteOfferIds, setNoteOfferIds,
    hasFavFilter, setHasFavFilter, onFavFilterToggle,
    promoFilter, setPromoFilter, promoFilterVisible, setPromoFilterVisible,
    filteredCount, totalCount, setProductCounts,
    storeScopeControl, setStoreScopeControl,
  }), [
    search, attrFilters, numFilters, columnFilters, sortKey, sortDir,
    hasNoteFilter, noteOfferIds, hasFavFilter, promoFilter, promoFilterVisible,
    filteredCount, totalCount, storeScopeControl,
    // Все коллбэки выше стабильны через useCallback([]),
    // их не нужно включать в deps.
    setAttrFilter, setAllAttrFilters, setNumFilter, clearNumFilter,
    setColumnFilter, setColumnValueSelection, clearColumnFilter, clearColumnFilters,
    clearAllFilters, setSort, setProductCounts, setPromoFilterVisible,
  ]);

  return (
    <HeaderContext.Provider value={value}>
      {children}
    </HeaderContext.Provider>
  );
}

export function useHeader() {
  const ctx = useContext(HeaderContext);
  if (!ctx) throw new Error("useHeader must be used within HeaderProvider");
  return ctx;
}
